import { useState } from "react";
import { doc, deleteDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../../../firebase/config";
import "../../../styles/DashboardStyles/Overlays/UserProfileModal.css";
import { sendPasswordResetEmail } from 'firebase/auth';

interface User {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  role: string;
  accountStatus: string;
  createdAt: any;
  updatedAt: any;
  userId: string;
}

interface Props {
  user: User;
  onClose: () => void;
  onDeleted: (id: string) => void;
  currentUserRole: string;
}

export default function UserProfileModal({ user, onClose, onDeleted, currentUserRole }: Props) {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<"success" | "error">("success");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [status, setStatus] = useState(user.accountStatus);
  const [statusLoading, setStatusLoading] = useState(false);
  const [role, setRole] = useState(user.role);
  const [roleLoading, setRoleLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  const currentUser = auth.currentUser;
  //const currentUserRole = /* pass this down as a prop */ props.currentUserRole;

  const isAdmin = currentUserRole === "admin";
  const isSuperAdmin = currentUserRole === "superadmin";

  const canEditRole =
  isSuperAdmin ||
  (isAdmin && user.role !== "superadmin");
  
  const canResetOwnPassword =
  (currentUserRole === "admin" || currentUserRole === "superadmin")
  && currentUser?.uid === user.id;

  // ── Format Firestore timestamp ──
  const formatDate = (timestamp: any) => {
    if (!timestamp) return "—";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-ZA", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // ── Delete User ──
  const handleDelete = async () => {
    setLoading(true);
    setMessage("");
    try {
      await deleteDoc(doc(db, "users", user.id));
      onDeleted(user.id);
      onClose();
    } catch (err) {
      setMessageType("error");
      setMessage("Failed to delete user. Check permissions.");
      setLoading(false);
    }
  };

  // ── Close on backdrop click ──
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleStatusChange = async (newStatus: string) => {
    setStatusLoading(true);
    setMessage("");
    try {
      await updateDoc(doc(db, "users", user.id), {
        accountStatus: newStatus,
        updatedAt: serverTimestamp()
      });
      setStatus(newStatus);
      setMessageType("success");
      setMessage(`Status updated to "${newStatus}" successfully.`);
    } catch (err) {
      setMessageType("error");
      setMessage("Failed to update status. Check permissions.");
    } finally {
      setStatusLoading(false);
    }
  };
  
  const handleRoleChange = async (newRole: string) => {

    if (!canEditRole) {
    alert("You do not have permission to change this role.");
    return;
  }

    setRoleLoading(true);
    setMessage("");
    try {
      await updateDoc(doc(db, "users", user.id), {
        role: newRole,
        updatedAt: serverTimestamp()
      });
      setRole(newRole);
      setMessageType("success");
      setMessage(`Role updated to "${newRole}" successfully.`);
    } catch (err) {
      setMessageType("error");
      setMessage("Failed to update role. Check permissions.");
    } finally {
      setRoleLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setPasswordLoading(true);
    setMessage("");
    try {
      await sendPasswordResetEmail(auth, user.email);
      setMessageType("success");
      setMessage(`Password reset email sent to ${user.email}.`);
    } catch (err) {
      setMessageType("error");
      setMessage("Failed to send reset email. Try again.");
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-card">

        {/* HEADER */}
        <div className="modal-header">
          <div className="modal-avatar">
            {user.fullName.charAt(0).toUpperCase()}
          </div>
          <div className="modal-header-info">
            <h2 className="modal-name">{user.fullName}</h2>
            <span className={`status-badge ${user.accountStatus}`}>
              {user.accountStatus}
            </span>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {/* DIVIDER */}
        <div className="modal-divider" />

        {/* BODY — USER DETAILS */}
        <div className="modal-body">
          <div className="modal-section-title">Account Information</div>
          <div className="modal-grid">

            <div className="modal-field">
              <span className="modal-field-label">Full Name</span>
              <span className="modal-field-value">{user.fullName}</span>
            </div>

            <div className="modal-field">
              <span className="modal-field-label">Email Address</span>
              <span className="modal-field-value">{user.email}</span>
            </div>

            <div className="modal-field">
              <span className="modal-field-label">Phone Number</span>
              <span className="modal-field-value">{user.phone || "—"}</span>
            </div>

            <div className="modal-field">
              <span className="modal-field-label">
                Role {roleLoading && "— Saving..."}
              </span>
              {isSuperAdmin || currentUserRole === "admin"? (
                // Superadmin sees a dropdown
                <select
                  className={`status-select ${role}`}
                  value={role}
                  disabled={roleLoading}
                  onChange={e => handleRoleChange(e.target.value)}
                >
                  <option value="client">Client</option>
                  <option value="admin">Admin</option>

                  {isSuperAdmin && (
                    <option value="superadmin">Super Admin</option>
                  )}
                </select>
              ) : (
                // Everyone else just sees the value
                <span className="modal-field-value">{role}</span>
              )}
            </div>

            <div className="modal-field">
              <span className="modal-field-label">
                Account Status {statusLoading && "— Saving..."}
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className={`status-badge ${status}`}>
                  {status}
                </span>
                <select
                  className={`status-select ${status}`}
                  value={status}
                  disabled={statusLoading}
                  onChange={e => handleStatusChange(e.target.value)}
                >
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="rejected">Rejected</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
            </div>

            <div className="modal-field">
              <span className="modal-field-label">User ID</span>
              <span className="modal-field-value modal-uid">{user.userId}</span>
            </div>

            <div className="modal-field">
              <span className="modal-field-label">Registered On</span>
              <span className="modal-field-value">{formatDate(user.createdAt)}</span>
            </div>

            <div className="modal-field">
              <span className="modal-field-label">Last Updated</span>
              <span className="modal-field-value">{formatDate(user.updatedAt)}</span>
            </div>

          </div>
        </div>

        {/* FEEDBACK MESSAGE */}
        {message && (
          <div className={`modal-message ${messageType}`}>
            {messageType === "success" ? "✓" : "⚠"} {message}
          </div>
        )}

        {/* DELETE CONFIRMATION */}
        {confirmDelete && (
          <div className="modal-confirm">
            <p>Are you sure you want to delete <strong>{user.fullName}</strong>? This cannot be undone.</p>
            <div className="modal-confirm-actions">
              <button
                className="btn-cancel"
                onClick={() => setConfirmDelete(false)}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="btn-confirm-delete"
                onClick={handleDelete}
                disabled={loading}
              >
                {loading ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        )}

        {/* FOOTER ACTIONS */}
        <div className="modal-footer">
          {/* Only show if admin is viewing their own account */}
          {canResetOwnPassword && (
            <button
              className="btn-reset"
              onClick={handleResetPassword}
              disabled={passwordLoading}
            >
              {passwordLoading ? "Sending..." : "↺ Reset Password"}
            </button>
          )}

          
          {/* Only superadmin can delete */}
          {isSuperAdmin && (
            <button
              className="btn-delete"
              onClick={() => setConfirmDelete(true)}
              disabled={loading}
            >
              🗑 Delete User
            </button>
          )}
        </div>

      </div>
    </div>
  );
}
